/**
 * OpportunityCard Component
 * Displays a single opportunity in card format for grid/list views
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Card, Badge } from 'react-bootstrap';

import useCapability from '@/shared/hooks/useCapability';

import { getTypeColor } from '../utils/opportunityColors';

import '../Opportunities.css';

// Icons for different opportunity types
const TYPE_ICONS = {
    Internship: '💼',
    'Job Shadow': '👥',
    Workshop: '🛠️',
    Conference: '🎤',
    'Grant Opportunity': '💰',
    'Funding Partnership': '🤝',
    Scholarship: '🎓',
    'Summer Program': '☀️',
    Camp: '🏕️',
    Competition: '🏆',
    Hackathon: '💻',
    Mentorship: '🧭',
    Webinar: '📺',
    'Networking Event': '🔗',
    default: '📋',
};

function formatDate(dateString) {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

export default function OpportunityCard({ opportunity, compact = false }) {
    const {
        _id,
        title,
        type,
        organization,
        location,
        dates,
        compensation,
        funding,
        imageUrl,
        targetAudience,
    } = opportunity;

    const typeColor = getTypeColor(type);
    const typeIcon = TYPE_ICONS[type] || TYPE_ICONS.default;

    const isRemote = location?.isRemote;
    const isInPerson = location?.isInPerson;
    const city = location?.city;

    const startDate = dates?.startDate ? formatDate(dates.startDate) : null;
    const isPaid = compensation?.isPaid;
    const isFunding = funding?.isFunding;
    // Opportunity partnerships are a per-tenant capability; only surface the
    // seeking-partners badge when the active ecosystem has it enabled.
    const partnershipsEnabled = useCapability('opportunityPartnerships');
    const seekingPartners = partnershipsEnabled && funding?.seekingPartners;

    return (
        <Card
            as={Link}
            to={`/opportunities/${_id}`}
            className={`h-100 border-0 shadow-sm opportunity-card text-decoration-none ${compact ? 'p-0' : ''}`}
        >
            {!compact && (
                <div className="position-relative">
                    {imageUrl ? (
                        <Card.Img
                            variant="top"
                            src={imageUrl}
                            alt={title}
                            className="opportunity-card-image"
                        />
                    ) : (
                        <div className="opportunity-card-placeholder">
                            <span>{typeIcon}</span>
                        </div>
                    )}
                    <Badge
                        bg=""
                        className="opportunity-type-badge type-color-badge"
                        /* CSS custom property for dynamic type color -- must be inline */
                        style={{ '--type-color': typeColor }}
                    >
                        {type}
                    </Badge>
                </div>
            )}

            <Card.Body className={compact ? 'p-3' : 'p-3'}>
                {compact && (
                    <div className="d-flex align-items-center gap-2 mb-2">
                        <span>{typeIcon}</span>
                        <Badge
                            bg=""
                            className="small type-color-badge"
                            /* CSS custom property for dynamic type color -- must be inline */
                            style={{ '--type-color': typeColor }}
                        >
                            {type}
                        </Badge>
                    </div>
                )}

                <Card.Title
                    className={`text-primary mb-2 ${compact ? 'h6' : 'h5'}`}
                >
                    {title}
                </Card.Title>

                {organization && (
                    <p className="text-muted small mb-2">{organization.name}</p>
                )}

                <div className="d-flex flex-wrap gap-1 mb-2">
                    {isRemote && (
                        <Badge bg="light" text="dark" className="small">
                            🌐 Remote
                        </Badge>
                    )}
                    {isInPerson && city && (
                        <Badge bg="light" text="dark" className="small">
                            📍 {city}
                        </Badge>
                    )}
                    {isPaid && (
                        <Badge bg="success" className="small">
                            💵 Paid
                        </Badge>
                    )}
                    {seekingPartners && (
                        <Badge bg="info" className="partner-badge small">
                            🤝 Seeking Partners
                        </Badge>
                    )}
                </div>

                {startDate && (
                    <p className="text-muted small mb-2">📅 {startDate}</p>
                )}

                {!compact && targetAudience && targetAudience.length > 0 && (
                    <div className="mt-auto">
                        <small className="text-muted">
                            For: {targetAudience.slice(0, 2).join(', ')}
                            {targetAudience.length > 2 &&
                                ` +${targetAudience.length - 2} more`}
                        </small>
                    </div>
                )}

                {isFunding && funding?.amount?.max && (
                    <p className="funding-amount mb-0 mt-2">
                        Up to ${funding.amount.max.toLocaleString()}
                    </p>
                )}
            </Card.Body>
        </Card>
    );
}

OpportunityCard.propTypes = {
    opportunity: PropTypes.shape({
        _id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        organization: PropTypes.shape({
            name: PropTypes.string,
            imageUrl: PropTypes.string,
        }),
        location: PropTypes.shape({
            isRemote: PropTypes.bool,
            isInPerson: PropTypes.bool,
            city: PropTypes.string,
        }),
        dates: PropTypes.shape({
            startDate: PropTypes.string,
        }),
        compensation: PropTypes.shape({
            isPaid: PropTypes.bool,
        }),
        funding: PropTypes.shape({
            isFunding: PropTypes.bool,
            seekingPartners: PropTypes.bool,
            amount: PropTypes.shape({
                max: PropTypes.number,
            }),
        }),
        imageUrl: PropTypes.string,
        targetAudience: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
    compact: PropTypes.bool,
};

OpportunityCard.defaultProps = {
    compact: false,
};
